/**
 * Frozen Artwork (#14) — Phase C API client.
 *
 * Consumes the backend-authoritative Artwork endpoints (Phase A/B). The frontend
 * NEVER computes footprint / CC / hash / placement validity — every authoritative
 * value comes from the backend preview/commit responses. The frozen canonical
 * content ({palette, rle}) is rendered deterministically (transparent sentinel
 * == palette.length — the ARTWORK canonical convention, distinct from the mutable
 * Canvas source convention).
 */
import { API_BASE } from "./api";
import { getCsrfToken, clearCsrfToken } from "./territory-identity-api";

export class ArtworkApiError extends Error {
  code: string;
  status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "ArtworkApiError";
    this.status = status;
    this.code = code;
  }
}

async function parse<T>(resp: Response): Promise<T> {
  const text = await resp.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!resp.ok) {
    const env = (body ?? {}) as { code?: string; message?: string; detail?: { code?: string; message?: string } };
    const code = env.code ?? env.detail?.code ?? "UNKNOWN";
    const message = env.message ?? env.detail?.message ?? resp.statusText;
    throw new ArtworkApiError(resp.status, code, message);
  }
  return body as T;
}

export type Rle = [number, number][];
export interface PixelContent {
  palette: string[];
  rle: Rle;
}
export interface AllocationItem {
  source_id: number;
  tier: string;
  amount: number;
}

export interface FreezePreview {
  wallet: string;
  allowed_status: boolean;
  effective_status: string;
  width: number;
  height: number;
  footprint: number;
  cc_required: number;
  recommended_substantial: boolean;
  min_footprint: number;
  content_version: number;
  content_hash: string;
  remaining_cc: number;
  sufficient_cc: boolean;
  proposed_allocation: AllocationItem[] | null;
  placement_valid: boolean;
  placement_overlap: boolean | null;
  can_freeze: boolean;
  warnings: string[];
  immutability_notice: string;
}

export interface ArtworkPlacement {
  placement_id?: string;
  anchor_x: number;
  anchor_y: number;
  width: number;
  height: number;
  rotation: number;
  rotation_millideg?: number;
  background?: string | null;
  empty_opacity_bps?: number;
  revision?: number;
}

export interface PlacementFull {
  placement_id: string;
  artwork_id: string;
  owner: string;
  anchor_x: number;
  anchor_y: number;
  width: number;
  height: number;
  rotation_millideg: number;
  rotation_deg: number;
  background: string | null;
  empty_opacity_bps: number;
  revision: number;
  active: boolean;
}

export interface PlacementPreview {
  artwork_id: string;
  is_owner: boolean;
  width: number;
  height: number;
  creative_footprint: number;
  reserved_cells: number;
  current_placement: PlacementFull;
  current_revision: number;
  normalized: {
    anchor_x: number; anchor_y: number; rotation_millideg: number;
    rotation_deg: number; background: string | null; empty_opacity_bps: number;
  } | null;
  cell_bbox: { cbx0: number; cby0: number; cbx1: number; cby1: number } | null;
  coverage_valid: boolean;
  collision: boolean | null;
  placement_valid: boolean;
  can_commit: boolean;
  warnings: string[];
  note: string;
}

export interface PlacementRequest {
  anchor_x: number;
  anchor_y: number;
  rotation_millideg: number;
  background: string | null;
  empty_opacity_bps: number;
  expected_revision?: number;
  idempotency_key?: string;
}

export interface ProvenanceEvent {
  artwork_id: string;
  seq: number;
  kind: string;
  actor: string;
  at: string;
  facts?: Record<string, unknown>;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
}

export interface PublicPlacement extends PlacementFull {
  content_hash: string;
  content_version: number;
  content: PixelContent;
  title: string | null;
  creator: string;
}

export interface ArtworkPublic {
  artwork_id: string;
  creator: string;
  owner: string;
  title: string;
  description: string | null;
  width: number;
  height: number;
  footprint: number;
  royalty_percent: number;
  status: string;
  content_version: number;
  content_hash: string;
  frozen_at: string;
  content?: PixelContent;
  placement?: ArtworkPlacement;
  idempotent_replay?: boolean;
}

export interface Collection {
  wallet: string;
  self: boolean;
  created_by: ArtworkPublic[];
  owned: ArtworkPublic[];
  created_artwork_count: number;
}

export interface FreezePreviewRequest {
  identity_id: string;
  element_id: string;
  source_x: number;
  source_y: number;
  width: number;
  height: number;
  anchor_x?: number;
  anchor_y?: number;
  allocation?: AllocationItem[];
}

export interface FreezeCommitRequest extends FreezePreviewRequest {
  anchor_x: number;
  anchor_y: number;
  royalty_percent: number;
  title: string;
  description?: string;
  idempotency_key: string;
}

export const artworkApi = {
  /** Read-only authoritative preview (POST; session + trusted-origin, no CSRF). */
  async previewFreeze(body: FreezePreviewRequest, signal?: AbortSignal): Promise<FreezePreview> {
    const resp = await fetch(`${API_BASE}/api/v1/artwork/self/freeze/preview`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
    return parse<FreezePreview>(resp);
  },

  /** Irreversible atomic Freeze commit (POST; session + trusted-origin + CSRF). */
  async commitFreeze(body: FreezeCommitRequest): Promise<ArtworkPublic> {
    const doPost = async (csrf: string) =>
      fetch(`${API_BASE}/api/v1/artwork/self/freeze`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-The-Grid-CSRF": csrf },
        body: JSON.stringify(body),
      });
    let resp = await doPost(await getCsrfToken());
    if (resp.status === 403) {
      const peek = await resp.clone().json().catch(() => null);
      const code = peek?.code ?? peek?.detail?.code;
      if (code === "CSRF_INVALID" || code === "CSRF_REQUIRED") {
        clearCsrfToken();
        resp = await doPost(await getCsrfToken(true));
      }
    }
    return parse<ArtworkPublic>(resp);
  },

  selfCollection: (signal?: AbortSignal): Promise<Collection> => {
    return fetch(`${API_BASE}/api/v1/artwork/self/collection`, {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      signal,
    }).then((r) => parse<Collection>(r));
  },

  getArtwork: (artworkId: string, signal?: AbortSignal): Promise<ArtworkPublic> => {
    return fetch(`${API_BASE}/api/v1/artwork/public/${encodeURIComponent(artworkId)}`, {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      signal,
    }).then((r) => parse<ArtworkPublic>(r));
  },

  // ── #15 Placement + Provenance ──
  previewPlacement: (artworkId: string, body: PlacementRequest, signal?: AbortSignal): Promise<PlacementPreview> => {
    return fetch(`${API_BASE}/api/v1/artwork/self/${encodeURIComponent(artworkId)}/placement/preview`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    }).then((r) => parse<PlacementPreview>(r));
  },

  async commitPlacement(artworkId: string, body: PlacementRequest): Promise<{ placement: PlacementFull; idempotent_replay: boolean; noop?: boolean }> {
    const doPost = async (csrf: string) =>
      fetch(`${API_BASE}/api/v1/artwork/self/${encodeURIComponent(artworkId)}/placement`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-The-Grid-CSRF": csrf },
        body: JSON.stringify(body),
      });
    let resp = await doPost(await getCsrfToken());
    if (resp.status === 403) {
      const peek = await resp.clone().json().catch(() => null);
      const code = peek?.code ?? peek?.detail?.code;
      if (code === "CSRF_INVALID" || code === "CSRF_REQUIRED") {
        clearCsrfToken();
        resp = await doPost(await getCsrfToken(true));
      }
    }
    return parse(resp);
  },

  getProvenance: (artworkId: string, signal?: AbortSignal): Promise<{ artwork_id: string; events: ProvenanceEvent[]; count: number }> => {
    return fetch(`${API_BASE}/api/v1/artwork/public/${encodeURIComponent(artworkId)}/provenance`, {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      signal,
    }).then((r) => parse(r));
  },

  publicPlacements: (x: number, y: number, w: number, h: number, signal?: AbortSignal): Promise<{ placements: PublicPlacement[]; count: number }> => {
    return fetch(`${API_BASE}/api/v1/artwork/public/placements?x=${x}&y=${y}&w=${w}&h=${h}`, {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      signal,
    }).then((r) => parse(r));
  },
};

/** Human-readable, honest labels for backend warning / error codes. */
export function artworkCodeLabel(code: string): string {
  const map: Record<string, string> = {
    ARTWORK_NOT_ALLOWED: "Only Artists and NeoArtists can freeze artworks.",
    ARTWORK_FOOTPRINT_TOO_SMALL: "Artwork must cover at least 100 cells (width × height).",
    ARTWORK_INSUFFICIENT_CC: "Not enough Creative Capacity for this footprint.",
    ARTWORK_CC_CONFLICT: "Capacity changed — re-check allocation and try again.",
    ARTWORK_PLACEMENT_OVERLAP: "This area overlaps another frozen artwork.",
    ARTWORK_PLACEMENT_NOT_OWNED: "Placement must sit fully on land you own.",
    ARTWORK_PLACEMENT_OUT_OF_BOUNDS: "Placement is outside the grid.",
    ARTWORK_PLACEMENT_INVALID: "Placement is not valid.",
    ARTWORK_PLACEMENT_STALE: "This placement changed since you opened it — reload and retry.",
    ARTWORK_NOT_OWNER: "Only the current owner can place this artwork.",
    ARTWORK_PLACEMENT_NOT_FOUND: "This artwork has no active placement.",
    ARTWORK_NOT_FOUND: "Artwork not found.",
    PLACEMENT_OUT_OF_BOUNDS: "The rotated artwork would leave the grid.",
    PLACEMENT_BACKGROUND_INVALID: "Background must be a hex colour or transparent.",
    PLACEMENT_OPACITY_INVALID: "Opacity must be between 0% and 100%.",
    ARTWORK_ROYALTY_INVALID: "Royalty must be exactly 3%, 4% or 5%.",
    ARTWORK_METADATA_INVALID: "Title/description is invalid.",
    ARTWORK_IDEMPOTENCY_INVALID: "Internal submit key invalid — reopen and retry.",
    ARTWORK_IDEMPOTENCY_CONFLICT: "A different submission is already in progress.",
    ARTWORK_SOURCE_NOT_FOUND: "Pixel source not found — save the canvas first.",
    ARTWORK_SOURCE_NOT_PIXEL_ART: "Only Pixel Studio artwork can be frozen.",
    UNTRUSTED_ORIGIN: "This request was blocked for security. Reload and try again.",
  };
  return map[code] ?? code;
}
